import React, { FC, useEffect, useState } from 'react';
import { PageTitle } from '../../layout/core';
import { adminBreadCrumbs } from '../../routing/adminRoutes';
import { ButtonComponent } from '../ButtonComponent';
import { CustomKeyDetailsForm } from '../api-clients/CustomKeyDetailsForm';
import { CustomKeyDetailsSecretKeysSection } from '../api-clients/CustomKeyDetailsSecretKeysSection';
import { CustomKeyClaims } from '../api-clients/CustomKeyClaims';
import { useParams } from 'react-router-dom';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import {
  fetchApiClientKeysDetail,
  fetchResourceList,
  setApiClientKeysDetails, setApiClientKeysDetailToDefault,
  useApiClientContext,
} from '../../store/api-client-context';
import { useFormik } from 'formik';
import {
  getCustomKeyDetailsSchema,
  getFormattedCustomKeyFormFields, getFormattedCustomKeyFormFValues, IFormattedProps,
  initialCustomKeyValues,
} from '../../constants/custom-key/custom-key-form';
import { isEqual } from 'lodash';
import { successAlert } from '../ToastMessage';
import { SectionWrapper } from '../SectionWrapper';
import { useTranslation } from 'react-i18next';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { ConfirmPasswordModal } from '../financial_accaunt/ConfirmPasswordModal';

export const PartnerDetailsApiKeyDetails: FC = () => {
  const { clientId } = useParams();
  const { t } = useTranslation();

  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
  const userData = authUser.userData;

  const { apiClientContextState, dispatch } = useApiClientContext();

  const {
    apiClientKeysDetails,
    isApiClientKeysDetailsLoading,
    isResourceListLoading,
    isSavingApiClientKeysDetailsLoading
  } = apiClientContextState;

  const [isLoginVisible, setIsLoginModalVisible] = useState<boolean>(false);
  const [selectedPermissions, setSelectedPermissions] = useState<{ [p: string]: string }>({});
  // const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

  const closeLoginModal = (): void => setIsLoginModalVisible(false);

  const formik = useFormik({
    initialValues: initialCustomKeyValues,
    validationSchema: getCustomKeyDetailsSchema,
    onSubmit: () => void 0
  });

  useEffect(() => {
    const offset = 5 * 60 * 1000; // 5 min
    if (userData?.loginDate && (new Date().getTime() - new Date(userData?.loginDate).getTime() <= offset )) {
      void fetchApiClientKeysDetail(dispatch, clientId, formik);
      void fetchResourceList(dispatch);
    } else {
      setIsLoginModalVisible(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      setApiClientKeysDetailToDefault(dispatch);
    };
  }, []);

  const handleSave = async (): Promise<void> => {
    formik.handleSubmit();
    if(!formik.isValid) return;
    const formattedValues = getFormattedCustomKeyFormFields(formik.values);
    const formattedInitialValues = getFormattedCustomKeyFormFValues(apiClientKeysDetails);
    formattedInitialValues.scopes?.sort();
    const scopes = Object.values(selectedPermissions || {})?.filter(el => el)?.sort();
    const body: IFormattedProps = {
      ...formattedValues,
      apiClient: apiClientKeysDetails?.clientId,
      scopes
    };
    if (isEqual(body, formattedInitialValues)) {
      successAlert({
        title: 'Information was successfully saved!'
      });
    } else {
      await setApiClientKeysDetails(dispatch, body, formik);
    }
  };

  // const handleCancelClick = (): void => {
  //   const formattedValues = getFormattedCustomKeyFormFields(formik.values);
  //   const formattedInitialValues = getFormattedCustomKeyFormFValues(apiClientKeysDetails);
  //   formattedInitialValues.scopes.sort();
  //   const scopes = Object.values(selectedPermissions).filter(el => el).sort();
  //   const data: IFormattedProps = {
  //     ...formattedValues,
  //     apiClient: apiClientKeysDetails?.clientId,
  //     scopes
  //   };
  //
  //   const initialValues = getInitialCustomKeyFormFields(apiClientKeysDetails);
  //   if (!isEqual(data, formattedInitialValues)) {
  //     setModalState({
  //       isOpen: true,
  //       modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
  //       modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
  //       confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
  //       onConfirm: closeModal,
  //       onCancel: (): void => {
  //         closeModal();
  //         setIsEditMode(false);
  //         formik.setValues(initialValues);
  //       }
  //     });
  //   } else {
  //     setIsEditMode(false);
  //   }
  // };

  const saveButton = (
    <ButtonComponent
      buttonName="Save"
      additionalClass="btn-sm w-100px"
      onButtonClick={():Promise<void> => !isSavingApiClientKeysDetailsLoading && handleSave()}
      type="submit"
      loading={isSavingApiClientKeysDetailsLoading}
    />
  );

  return (
    <>
      <PageTitle breadcrumbs={adminBreadCrumbs}>
        {clientId === 'create-key' ? 'Create custom key' : clientId}
      </PageTitle>

      <SectionWrapper
        title={t('PARTNERS.PARTNER_API_KEY_DETAILS')}
        sectionAdditionalClass="mb-5 min-height-200"
        pageLoading={isResourceListLoading || isApiClientKeysDetailsLoading}
        headerAdditionalBlock={saveButton}
        isCardBody
      >
        <div className="d-xl-flex">
          <CustomKeyDetailsForm formik={formik} isEditMode={true}/>
          <CustomKeyDetailsSecretKeysSection />
        </div>

        <CustomKeyClaims setSelectedPermissions={setSelectedPermissions} isEditMode={true}/>
      </SectionWrapper>

      <ConfirmPasswordModal
        isLoginVisible={isLoginVisible}
        closeModal={closeLoginModal}
        fetchData={async (): Promise<void> => {
          await fetchApiClientKeysDetail(dispatch, clientId, formik);
          await fetchResourceList(dispatch);
        }}
      />

      <ModalWindow
        {...modalState}
        onClose={closeModal}
      />
    </>
  );
};